import { useEffect } from 'react'
import { OG_IMAGE_URL, SITE_URL, upsertLink, upsertMeta, upsertJsonLd, removeJsonLd, breadcrumbJsonLd, webApplicationJsonLd } from './useSeo'

const JSON_LD_ID = 'pv-kostenrechner-jsonld'
const APP_JSON_LD_ID = 'pv-kostenrechner-webapp-jsonld'
const BREADCRUMB_ID = 'pv-kostenrechner-breadcrumb'
const TITLE = 'PV-Kostenrechner: Photovoltaik-Preis in Sekunden berechnen | Roofora'
const DESCRIPTION =
  'Kostenloser PV-Kostenrechner: Durchschnittspreis pro kWp, mit oder ohne Batteriespeicher – ' +
  'ohne Anmeldung, ohne Kontaktdaten, basierend auf echten Handwerker-Angeboten.'
/** tasks/new-things/preis-durchschnittsrechner/05-seo-landingpages.md - aus echter
 *  Keyword-Recherche (30.07.2026), nicht aus dem Ursprungstext übernommen. */
const KEYWORDS = [
  'Photovoltaik Kostenrechner',
  'PV Kostenrechner',
  'PV Preisrechner',
  'Photovoltaik Kosten pro kWp',
  'PV Preis pro kWp',
  'Kosten pro Kilowattpeak',
  'Batteriespeicher Kosten pro kWh',
  'Stromspeicher Preise Photovoltaik',
  'Photovoltaik Preisvergleich regional',
  'PV Kostenrechner ohne Anmeldung',
  'Photovoltaik Anlage Preis berechnen kostenlos',
  'Solaranlage Preisrechner',
  'PV Durchschnittspreis',
  'Solaranlage Durchschnittspreis',
]

export interface PvKostenRechnerFaqItem {
  q: string
  a: string
}

/** SEO tags for the public, anonymous /pv-kostenrechner page - German-only and static like
 *  usePvRechnerSeo (same reasoning: no i18n-translated page content), plus a FAQPage schema built
 *  from the page's own FAQ content (same shape as useLandingPageSeo's, which isn't reusable as-is
 *  since it's built around the generic LandingPageContent shape this bespoke page doesn't use). */
export function usePvKostenRechnerSeo(faq: PvKostenRechnerFaqItem[]) {
  useEffect(() => {
    document.documentElement.lang = 'de'
    const canonicalUrl = `${SITE_URL}/pv-kostenrechner`

    document.title = TITLE
    upsertMeta('name', 'description', DESCRIPTION)
    upsertMeta('name', 'keywords', KEYWORDS.join(', '))
    upsertMeta('property', 'og:title', TITLE)
    upsertMeta('property', 'og:description', DESCRIPTION)
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('property', 'og:image', OG_IMAGE_URL)
    upsertMeta('name', 'twitter:title', TITLE)
    upsertMeta('name', 'twitter:description', DESCRIPTION)
    upsertMeta('name', 'twitter:image', OG_IMAGE_URL)
    upsertLink('canonical', canonicalUrl)

    upsertJsonLd(JSON_LD_ID, {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faq.map((item) => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a },
      })),
    })

    upsertJsonLd(
      BREADCRUMB_ID,
      breadcrumbJsonLd([
        { name: 'Roofora', url: `${SITE_URL}/de` },
        { name: 'PV-Kostenrechner', url: canonicalUrl },
      ]),
    )

    // Same shape as usePvRechnerSeo's WebApplication schema (docs/tasks/active/seo/seo-2.0.md #17: "Schema.org"
    // was listed as a required element for this page, and only FAQPage existed so far).
    upsertJsonLd(APP_JSON_LD_ID, webApplicationJsonLd('Roofora PV-Kostenrechner', DESCRIPTION, canonicalUrl))

    return () => {
      removeJsonLd(JSON_LD_ID)
      removeJsonLd(APP_JSON_LD_ID)
      removeJsonLd(BREADCRUMB_ID)
    }
  }, [faq])
}
